"use client"

import { useState, useEffect, useContext } from "react"
import {
  Box,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AddIcon from "@mui/icons-material/Add"
import mainContext from "../../contexts/mainContext"
import TextEditor from "../Common/TextEditor"
import { getFAQsController, addEditFAQController, removeFAQController } from "../../Controllers/FAQs"
import { __snackBarSeverity } from "../../constants"
import "./faqs.css"

const FAQBase = ({ title, faqType = null, showTypeSelector = false }) => {
  const { setProfileNavTitle, setSnackBar, setPageload } = useContext(mainContext)
  const [faqs, setFaqs] = useState([])
  const [openDialog, setOpenDialog] = useState(false)
  const [dialogMode, setDialogMode] = useState("add") // "add" or "edit"
  const [currentFAQ, setCurrentFAQ] = useState({ faqId: null, question: "", answer: "", type: faqType || "Customer" })
  const [expanded, setExpanded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [faqToDelete, setFaqToDelete] = useState(null)

  useEffect(() => {
   // setProfileNavTitle(title || "FAQ Management")
    fetchFAQs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqType])

  const fetchFAQs = async () => {
    setPageload(true)
    try {
      const response = await getFAQsController(null, faqType)
      if (response && response.data && response.data.status === 200) {
        console.log("FAQs response:", response.data.data.faqs)
        setFaqs(response.data.data.faqs || [])
      } else {
        callSnackBar("Failed to fetch FAQs", "error")
      }
    } catch (error) {
      console.error("Error fetching FAQs:", error)
      callSnackBar("An error occurred while fetching FAQs", "error")
    } finally {
      setPageload(false)
    }
  }

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false)
  }

  const handleOpenDialog = (mode, faq = { _id: null, question: "", answer: "", type: faqType || "Customer" }) => {
    setDialogMode(mode)
    setCurrentFAQ({
      faqId: faq._id || faq.id, // Handle both _id and id
      question: faq.question || "",
      answer: faq.answer || "",
      type: faq.type || faqType || "Customer",
    })
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setCurrentFAQ({ faqId: null, question: "", answer: "", type: faqType || "Customer" })
  }

  const handleQuestionChange = (e) => {
    setCurrentFAQ({ ...currentFAQ, question: e.target.value })
  }

  const handleTypeChange = (e) => {
    setCurrentFAQ({ ...currentFAQ, type: e.target.value })
  }

  const handleAnswerUpdate = (content) => {
    setCurrentFAQ({ ...currentFAQ, answer: content })
  }

  const handleSaveFAQ = async () => {
    if (!currentFAQ.question.trim()) {
      callSnackBar("Question cannot be empty", "error")
      return
    }

    if (!currentFAQ.answer.trim()) {
      callSnackBar("Answer cannot be empty", "error")
      return
    }

    if (!currentFAQ.type) {
      callSnackBar("FAQ type must be selected", "error")
      return
    }

    setLoading(true)
    try {
      const response = await addEditFAQController({
        faqId: currentFAQ.faqId, // null for new, ID for edit
        question: currentFAQ.question,
        answer: currentFAQ.answer,
        type: currentFAQ.type,
      })

      if (response && response.data && response.data.status === 200) {
        callSnackBar(currentFAQ.faqId ? "FAQ updated successfully" : "FAQ added successfully", "success")
        fetchFAQs()
        handleCloseDialog()
      } else {
        callSnackBar(currentFAQ.faqId ? "Failed to update FAQ" : "Failed to add FAQ", "error")
      }
    } catch (error) {
      console.error("Error saving FAQ:", error)
      callSnackBar("An error occurred", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClick = (faq) => {
    setFaqToDelete(faq)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!faqToDelete) return

    setLoading(true)
    try {
      // Use _id or id, whichever is available
      const faqId = faqToDelete._id || faqToDelete.id
      const response = await removeFAQController(faqId)

      if (response && response.data && response.data.status === 200) {
        callSnackBar("FAQ deleted successfully", "success")
        fetchFAQs()
      } else {
        callSnackBar("Failed to delete FAQ", "error")
      }
    } catch (error) {
      console.error("Error deleting FAQ:", error)
      callSnackBar("An error occurred while deleting", "error")
    } finally {
      setLoading(false)
      setDeleteConfirmOpen(false)
      setFaqToDelete(null)
    }
  }

  const callSnackBar = (msg, type) => {
    setSnackBar({
      status: true,
      text: msg,
      severity: __snackBarSeverity[type],
    })
  }

  return (
    <Paper className="faq-container" elevation={1}>
      <Box className="faq-header">
        <Typography variant="h6" component="h1">
          {title || "Frequently Asked Questions"}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog("add")}
          sx={{ textTransform: "none" }}
        >
          Add FAQ
        </Button>
      </Box>

      <Box className="faq-list">
        {faqs.length === 0 ? (
          <Typography className="no-faqs">No FAQs found. Click "Add FAQ" to create one.</Typography>
        ) : (
          faqs.map((faq) => (
            <Accordion
              key={faq._id || faq.id}
              expanded={expanded === `panel-${faq._id || faq.id}`}
              onChange={handleAccordionChange(`panel-${faq._id || faq.id}`)}
              className="faq-accordion"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="faq-summary">
                <Box className="question-container">
                  <Typography>{faq.question}</Typography>
                  <Box className="action-buttons">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleOpenDialog("edit", faq)
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteClick(faq)
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails className="faq-details">
                <div dangerouslySetInnerHTML={{ __html: faq.answer }} />
                {showTypeSelector && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: "block" }}>
                    Type: {faq.type || "Not specified"}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>

      {/* Add/Edit FAQ Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{dialogMode === "add" ? "Add New FAQ" : "Edit FAQ"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Question"
            type="text"
            fullWidth
            value={currentFAQ.question}
            onChange={handleQuestionChange}
            sx={{ mb: 2 }}
          />

          {showTypeSelector && (
            <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
              <InputLabel id="faq-type-label">FAQ Type</InputLabel>
              <Select labelId="faq-type-label" value={currentFAQ.type} label="FAQ Type" onChange={handleTypeChange}>
                <MenuItem value="Customer">Customer</MenuItem>
                <MenuItem value="HouseKeeper">HouseKeeper</MenuItem>
                <MenuItem value="BabySitter">BabySitter</MenuItem>
              </Select>
              <FormHelperText>Select the user type this FAQ is for</FormHelperText>
            </FormControl>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Answer
          </Typography>
          <TextEditor data={currentFAQ.answer} hanldeUpdate={handleAnswerUpdate} />
          <Typography variant="caption" color="textSecondary" >
            Press "Add / Update" to save the answer.
         </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveFAQ}
            color="primary"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this FAQ? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="primary" disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default FAQBase
